/**
 * ErrorHandler - Manages error display and user interaction for error recovery
 * Handles location permission errors, calculation failures, and offline scenarios
 */

/**
 * ErrorHandler class for managing error UI components
 */
export class ErrorHandler {
  constructor() {
    // DOM elements
    this.elements = {
      // Status elements
      locationStatus: document.getElementById('location-status'),
      connectionStatus: document.getElementById('connection-status'),
      dataWarning: document.getElementById('data-warning'),
      warningTime: document.getElementById('warning-time'),
      
      // Error containers
      errorContainer: document.getElementById('error-container'),
      locationError: document.getElementById('location-error'),
      calculationError: document.getElementById('calculation-error'),
      generalError: document.getElementById('general-error'),
      generalErrorText: document.getElementById('general-error-text'),
      
      // Manual location form
      manualLocationForm: document.getElementById('manual-location-form'),
      locationForm: document.getElementById('location-form'),
      cityInput: document.getElementById('city-input'),
      latitudeInput: document.getElementById('latitude-input'),
      longitudeInput: document.getElementById('longitude-input'),
      formError: document.getElementById('form-error'),
      
      // Buttons
      locationRetryButton: document.getElementById('location-retry-button'),
      calculationRetryButton: document.getElementById('calculation-retry-button'),
      generalRetryButton: document.getElementById('general-retry-button'),
      manualLocationButton: document.getElementById('manual-location-button'),
      closeManualForm: document.getElementById('close-manual-form'),
      submitLocation: document.getElementById('submit-location'),
      cancelLocation: document.getElementById('cancel-location')
    };
    
    // Callbacks
    this.callbacks = {
      onLocationRetry: null,
      onCalculationRetry: null,
      onGeneralRetry: null,
      onManualLocation: null
    };
    
    // State
    this.isOnline = navigator.onLine;
    this.lastDataUpdate = null;
    this.staleDataThreshold = 5 * 60 * 1000; // 5 minutes
    
    // Initialize
    this.setupEventListeners();
    this.setupOnlineOfflineHandlers();
  }

  /**
   * Set up event listeners for error handling UI
   */
  setupEventListeners() {
    // Retry buttons
    if (this.elements.locationRetryButton) {
      this.elements.locationRetryButton.addEventListener('click', () => {
        this.hideLocationError();
        if (this.callbacks.onLocationRetry) {
          this.callbacks.onLocationRetry();
        }
      });
    }

    if (this.elements.calculationRetryButton) {
      this.elements.calculationRetryButton.addEventListener('click', () => {
        this.hideCalculationError();
        if (this.callbacks.onCalculationRetry) {
          this.callbacks.onCalculationRetry();
        }
      });
    }

    if (this.elements.generalRetryButton) {
      this.elements.generalRetryButton.addEventListener('click', () => {
        this.hideGeneralError();
        if (this.callbacks.onGeneralRetry) {
          this.callbacks.onGeneralRetry();
        }
      });
    }

    // Manual location button
    if (this.elements.manualLocationButton) {
      this.elements.manualLocationButton.addEventListener('click', () => {
        this.showManualLocationForm();
      });
    }

    // Manual location form handlers
    if (this.elements.closeManualForm) {
      this.elements.closeManualForm.addEventListener('click', () => {
        this.hideManualLocationForm();
      });
    }

    if (this.elements.cancelLocation) {
      this.elements.cancelLocation.addEventListener('click', () => {
        this.hideManualLocationForm();
      });
    }

    if (this.elements.locationForm) {
      this.elements.locationForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleManualLocationSubmit();
      });
    }

    // Input validation
    if (this.elements.cityInput) {
      this.elements.cityInput.addEventListener('input', () => {
        this.clearFormError();
        this.validateCityInput();
      });
    }

    if (this.elements.latitudeInput) {
      this.elements.latitudeInput.addEventListener('input', () => {
        this.clearFormError();
        this.validateCoordinateInputs();
      });
    }

    if (this.elements.longitudeInput) {
      this.elements.longitudeInput.addEventListener('input', () => {
        this.clearFormError();
        this.validateCoordinateInputs();
      });
    }

    // Close form on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isManualLocationFormVisible()) {
        this.hideManualLocationForm();
      }
    });
  }

  /**
   * Set up online/offline event handlers
   */
  setupOnlineOfflineHandlers() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.hideConnectionStatus();
      console.log('Connection restored');
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.showOfflineStatus();
      console.log('Connection lost');
    });
  }

  /**
   * Show location permission error
   * @param {string} message - Optional custom error message
   */
  showLocationError(message = null) {
    if (this.elements.locationError) {
      if (message) {
        const errorText = this.elements.locationError.querySelector('.error-text');
        if (errorText) {
          errorText.textContent = message;
        }
      }
      
      this.elements.locationError.style.display = 'block';
      this.showErrorContainer();
      
      // Focus the retry button for accessibility
      setTimeout(() => {
        if (this.elements.locationRetryButton) {
          this.elements.locationRetryButton.focus();
        }
      }, 100);
    }
  }

  /**
   * Hide location permission error
   */
  hideLocationError() {
    if (this.elements.locationError) {
      this.elements.locationError.style.display = 'none';
      this.hideErrorContainerIfEmpty();
    }
  }

  /**
   * Show calculation error
   * @param {string} message - Optional custom error message
   */
  showCalculationError(message = null) {
    if (this.elements.calculationError) {
      if (message) {
        const errorText = this.elements.calculationError.querySelector('.error-text');
        if (errorText) {
          errorText.textContent = message;
        }
      }
      
      this.elements.calculationError.style.display = 'block';
      this.showErrorContainer();
      
      // Focus the retry button for accessibility
      setTimeout(() => {
        if (this.elements.calculationRetryButton) {
          this.elements.calculationRetryButton.focus();
        }
      }, 100);
    }
  }

  /**
   * Hide calculation error
   */
  hideCalculationError() {
    if (this.elements.calculationError) {
      this.elements.calculationError.style.display = 'none';
      this.hideErrorContainerIfEmpty();
    }
  }

  /**
   * Show general error
   * @param {string} message - Error message to display
   */
  showGeneralError(message) {
    if (this.elements.generalError && this.elements.generalErrorText) {
      this.elements.generalErrorText.textContent = message;
      this.elements.generalError.style.display = 'block';
      this.showErrorContainer();
      
      // Focus the retry button for accessibility
      setTimeout(() => {
        if (this.elements.generalRetryButton) {
          this.elements.generalRetryButton.focus();
        }
      }, 100);
    }
  }

  /**
   * Hide general error
   */
  hideGeneralError() {
    if (this.elements.generalError) {
      this.elements.generalError.style.display = 'none';
      this.hideErrorContainerIfEmpty();
    }
  }

  /**
   * Hide all errors
   */
  hideAllErrors() {
    this.hideLocationError();
    this.hideCalculationError();
    this.hideGeneralError();
  }

  /**
   * Show offline status indicator
   */
  showOfflineStatus() {
    if (this.elements.connectionStatus) {
      const indicator = this.elements.connectionStatus.querySelector('.connection-indicator');
      const text = this.elements.connectionStatus.querySelector('.connection-text');
      
      if (indicator) {
        indicator.className = 'connection-indicator offline';
      }
      
      if (text) {
        text.textContent = "You're offline - using cached data";
      }
      
      this.elements.connectionStatus.style.display = 'flex';
    }
  }

  /**
   * Hide connection status
   */
  hideConnectionStatus() {
    if (this.elements.connectionStatus) {
      this.elements.connectionStatus.style.display = 'none';
    }
  }

  /**
   * Show data staleness warning
   * @param {Date} lastUpdate - When data was last updated
   */
  showDataWarning(lastUpdate) {
    if (this.elements.dataWarning && this.elements.warningTime) {
      const age = Date.now() - lastUpdate.getTime();
      const minutes = Math.floor(age / (1000 * 60));
      
      let timeText;
      if (minutes < 60) {
        timeText = `${minutes}m ago`;
      } else {
        const hours = Math.floor(minutes / 60);
        timeText = `${hours}h ago`;
      }
      
      this.elements.warningTime.textContent = timeText;
      this.elements.dataWarning.style.display = 'flex';
    }
  }

  /**
   * Hide data warning
   */
  hideDataWarning() {
    if (this.elements.dataWarning) {
      this.elements.dataWarning.style.display = 'none';
    }
  }

  /**
   * Update data freshness indicators
   * @param {Date} lastUpdate - When data was last updated
   */
  updateDataFreshness(lastUpdate) {
    this.lastDataUpdate = lastUpdate;
    
    if (!lastUpdate) {
      this.hideDataWarning();
      return;
    }
    
    const age = Date.now() - lastUpdate.getTime();
    
    if (age > this.staleDataThreshold) {
      this.showDataWarning(lastUpdate);
    } else {
      this.hideDataWarning();
    }
  }

  /**
   * Show manual location entry form
   */
  showManualLocationForm() {
    if (this.elements.manualLocationForm) {
      this.elements.manualLocationForm.style.display = 'block';
      this.clearFormInputs();
      this.clearFormError();
      
      // Focus the city input for accessibility
      setTimeout(() => {
        if (this.elements.cityInput) {
          this.elements.cityInput.focus();
        }
      }, 100);
    }
  }

  /**
   * Hide manual location entry form
   */
  hideManualLocationForm() {
    if (this.elements.manualLocationForm) {
      this.elements.manualLocationForm.style.display = 'none';
      this.clearFormInputs();
      this.clearFormError();
    }
  }

  /**
   * Check if manual location form is visible
   * @returns {boolean} True if form is visible
   */
  isManualLocationFormVisible() {
    return this.elements.manualLocationForm && 
           this.elements.manualLocationForm.style.display !== 'none';
  }

  /**
   * Handle manual location form submission
   */
  async handleManualLocationSubmit() {
    const cityValue = this.elements.cityInput?.value.trim();
    const latValue = this.elements.latitudeInput?.value.trim();
    const lngValue = this.elements.longitudeInput?.value.trim();
    
    // Validate inputs
    if (!cityValue && (!latValue || !lngValue)) {
      this.showFormError('Please enter either a city name or coordinates');
      return;
    }
    
    if (latValue || lngValue) {
      if (!this.validateCoordinates(latValue, lngValue)) {
        return; // Error already shown by validateCoordinates
      }
    }
    
    try {
      let coordinates;
      
      if (latValue && lngValue) {
        // Use provided coordinates
        coordinates = {
          latitude: parseFloat(latValue),
          longitude: parseFloat(lngValue),
          accuracy: 1000, // Assume 1km accuracy for manual entry
          timestamp: new Date(),
          source: 'manual'
        };
      } else {
        // Try to geocode the city name
        coordinates = await this.geocodeCity(cityValue);
      }
      
      // Call the callback with the coordinates
      if (this.callbacks.onManualLocation) {
        await this.callbacks.onManualLocation(coordinates);
      }
      
      // Hide the form on success
      this.hideManualLocationForm();
      
    } catch (error) {
      console.error('Manual location submission failed:', error);
      this.showFormError(error.message || 'Failed to process location');
    }
  }

  /**
   * Geocode city name to coordinates (placeholder implementation)
   * @param {string} cityName - City name to geocode
   * @returns {Promise<Object>} Promise resolving to coordinates
   */
  async geocodeCity(cityName) {
    // This is a placeholder implementation
    // In a real app, you would use a geocoding service like:
    // - Google Geocoding API
    // - OpenStreetMap Nominatim
    // - Mapbox Geocoding API
    
    throw new Error('City geocoding is not implemented. Please use coordinates instead.');
  }

  /**
   * Validate coordinate inputs
   * @param {string} lat - Latitude string
   * @param {string} lng - Longitude string
   * @returns {boolean} True if valid
   */
  validateCoordinates(lat, lng) {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    
    if (isNaN(latitude) || isNaN(longitude)) {
      this.showFormError('Please enter valid numbers for coordinates');
      return false;
    }
    
    if (latitude < -90 || latitude > 90) {
      this.showFormError('Latitude must be between -90 and 90');
      return false;
    }
    
    if (longitude < -180 || longitude > 180) {
      this.showFormError('Longitude must be between -180 and 180');
      return false;
    }
    
    return true;
  }

  /**
   * Validate city input
   */
  validateCityInput() {
    const cityValue = this.elements.cityInput?.value.trim();
    
    if (cityValue && cityValue.length < 2) {
      // Don't show error for very short inputs, user is still typing
      return false;
    }
    
    return true;
  }

  /**
   * Validate coordinate inputs
   */
  validateCoordinateInputs() {
    const latValue = this.elements.latitudeInput?.value.trim();
    const lngValue = this.elements.longitudeInput?.value.trim();
    
    // If both are empty, that's fine
    if (!latValue && !lngValue) {
      return true;
    }
    
    // If one is filled, both should be filled
    if ((latValue && !lngValue) || (!latValue && lngValue)) {
      // Don't show error immediately, user might still be typing
      return false;
    }
    
    // If both are filled, validate them
    if (latValue && lngValue) {
      return this.validateCoordinates(latValue, lngValue);
    }
    
    return true;
  }

  /**
   * Show form error
   * @param {string} message - Error message to display
   */
  showFormError(message) {
    if (this.elements.formError) {
      const errorText = this.elements.formError.querySelector('.form-error-text');
      if (errorText) {
        errorText.textContent = message;
      }
      this.elements.formError.style.display = 'block';
    }
  }

  /**
   * Clear form error
   */
  clearFormError() {
    if (this.elements.formError) {
      this.elements.formError.style.display = 'none';
    }
  }

  /**
   * Clear form inputs
   */
  clearFormInputs() {
    if (this.elements.cityInput) {
      this.elements.cityInput.value = '';
    }
    if (this.elements.latitudeInput) {
      this.elements.latitudeInput.value = '';
    }
    if (this.elements.longitudeInput) {
      this.elements.longitudeInput.value = '';
    }
  }

  /**
   * Show error container
   */
  showErrorContainer() {
    if (this.elements.errorContainer) {
      this.elements.errorContainer.style.display = 'block';
    }
  }

  /**
   * Hide error container if no errors are visible
   */
  hideErrorContainerIfEmpty() {
    if (!this.elements.errorContainer) return;
    
    const visibleErrors = this.elements.errorContainer.querySelectorAll('.error-message[style*="block"]');
    
    if (visibleErrors.length === 0) {
      this.elements.errorContainer.style.display = 'none';
    }
  }

  /**
   * Set callback for location retry
   * @param {Function} callback - Callback function
   */
  onLocationRetry(callback) {
    this.callbacks.onLocationRetry = callback;
  }

  /**
   * Set callback for calculation retry
   * @param {Function} callback - Callback function
   */
  onCalculationRetry(callback) {
    this.callbacks.onCalculationRetry = callback;
  }

  /**
   * Set callback for general retry
   * @param {Function} callback - Callback function
   */
  onGeneralRetry(callback) {
    this.callbacks.onGeneralRetry = callback;
  }

  /**
   * Set callback for manual location entry
   * @param {Function} callback - Callback function
   */
  onManualLocation(callback) {
    this.callbacks.onManualLocation = callback;
  }

  /**
   * Get current online status
   * @returns {boolean} True if online
   */
  isOnlineStatus() {
    return this.isOnline;
  }

  /**
   * Clean up resources
   */
  destroy() {
    // Remove event listeners if needed
    this.callbacks = {};
  }
}

export default ErrorHandler;